// The customer's purchase order on a job (F-29), "Customer PO 4471", or "Customer PO required" when the
// bill-to needs one and there is none yet, with Record / Change for those who may (invoices.setPo,
// invoices.lines). "Customer" keeps it apart from our own POs to vendors (PO-6) on the same sheet. It
// can change until the invoice is paid; the server says so when it can't.

import { useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { SaveButton, useSaveKey } from './SaveButton.tsx';
import './po.css';

export function PoNumber({
  jobId,
  poNumber,
  required,
  canEdit,
  onSaved,
}: {
  jobId: string;
  poNumber: string | null;
  /** The job's bill-to requires a PO (Review won't complete without it). */
  required: boolean;
  canEdit: boolean;
  onSaved: (said: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(poNumber ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(`po:${jobId}:${value.trim()}`);
  const record = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('invoices.setPo', { jobId, poNumber: value.trim() }, { idempotencyKey: save.key });
      setOpen(false);
      onSaved(`Customer PO ${value.trim()} recorded.`);
    } catch (e) {
      // the server's words: a paid or void invoice takes no PO
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (open)
    return (
      <form
        className="row po-edit"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && value.trim()) void record();
        }}
      >
        <label className="f">
          Customer’s PO number
          <input
            className="field"
            value={value}
            maxLength={60}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            autoComplete="off"
            autoFocus
          />
        </label>
        <SaveButton
          busy={busy}
          busyLabel="Saving…"
          error={error}
          disabled={!value.trim()}
          onClick={() => void record()}
        >
          Save the PO
        </SaveButton>
        <button type="button" className="btn quiet" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </form>
    );
  if (!poNumber && !required && !canEdit) return null;
  return (
    <span className="po">
      {poNumber ? (
        <span className="chip">Customer PO {poNumber}</span>
      ) : required ? (
        <span className="chip warn">Customer PO required</span>
      ) : null}
      {canEdit && (
        <button
          type="button"
          className="btn quiet sm"
          onClick={() => {
            setValue(poNumber ?? '');
            setOpen(true);
          }}
        >
          {poNumber
            ? 'Change the customer’s PO'
            : required
              ? 'Record the customer’s PO'
              : 'Add the customer’s PO'}
        </button>
      )}
    </span>
  );
}
